import { useEventLogging } from '@innovamat/event-logging';
import type { OptionType } from '@innovamat/glimmer-components';

import { useCallback, useMemo, useState } from 'react';
import type { ModeLegend } from '../../../components/legend-fluency/legend-fluency';
import {
  CARD_STATUS,
  type CardStatus,
  type DeckReport,
  type StudentReport,
} from '../../../modules/fluency/deck-report';

type Student = StudentReport & {
  isInClassroomCourse: boolean;
};

type UseFluencyDashboard = {
  mode: ModeLegend;
  selectedStudent: string;
  handleChange: (option: OptionType | null) => void;
  allStudents: Student[];
  adaptedStudents: Student[];
  fluencyDashboardReports: DeckReport[];
  tableUpdateLoading: boolean;
  areAllCellsLocked: (
    operationTables: Array<{ cards: Array<{ status: CardStatus }> }>
  ) => boolean;
  showFluencyStatusTooltip: (deck: DeckReport) => {
    allGroupsLocked: boolean;
    noStudentsConnected: boolean;
  };
};

const FAKE_LOADING_TIME = 500;

export const useFluencyDashboard = (
  deckReports: DeckReport[]
): UseFluencyDashboard => {
  const [mode, setMode] = useState<ModeLegend>('allClassroomMode');
  const [selectedStudent, setSelectedStudent] = useState<string>('');
  const [tableUpdateLoading, setTableUpdateLoading] = useState(false);

  const { setEventData } = useEventLogging();

  const fluencyDashboardReports = useMemo(() => {
    if (mode === 'studentMode' && selectedStudent) {
      const decksWithStudent = deckReports.filter((deck) =>
        deck.students.some((s) => s.uuid === selectedStudent)
      );

      return decksWithStudent.map((deck) => {
        const selectedStudentData = deck.students.find(
          (s) => s.uuid === selectedStudent
        );

        return {
          ...deck,
          operationTables: deck.operationTables.map((table) => ({
            ...table,
            cards: table.cards.map((card) => {
              const cardStatus =
                selectedStudentData?.cards.find(
                  (studentCard) => studentCard.uuid === card.uuid
                )?.status || CARD_STATUS.LOCKED;

              return {
                ...card,
                status: cardStatus,
                studentsFailed: [],
                studentsAttempted: [],
              };
            }),
          })),
        };
      });
    }

    const filteredDecks = deckReports.filter(
      (deck) => deck.isInClassroomCourse
    );
    return filteredDecks;
  }, [deckReports, selectedStudent, mode]);

  const allStudents = deckReports
    .flatMap((report) => report.students || [])
    .reduce((uniqueStudents, student) => {
      const existingStudent = uniqueStudents.find(
        (s) => s.uuid === student.uuid
      );
      if (!existingStudent) {
        uniqueStudents.push({
          ...student,
          isInClassroomCourse: deckReports.some(
            (deck) =>
              deck.isInClassroomCourse &&
              deck.students.some((s) => s.uuid === student.uuid)
          ),
        });
      }
      return uniqueStudents;
    }, [] as Student[])
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  const adaptedStudents = useMemo(
    () => allStudents.filter((student) => !student.isInClassroomCourse),
    [allStudents]
  );

  const startTableLoading = useCallback((): void => {
    setTableUpdateLoading(true);
    setTimeout(() => {
      setTableUpdateLoading(false);
    }, FAKE_LOADING_TIME);
  }, []);

  const areAllCellsLocked = (
    operationTables: Array<{ cards: Array<{ status: CardStatus }> }>
  ): boolean => {
    return operationTables.every((table) =>
      table.cards.every((card) => card.status === CARD_STATUS.LOCKED)
    );
  };

  const hasNoStudentsConnected = (
    operationTables: Array<{ cards: Array<{ status: CardStatus }> }>
  ): boolean => {
    return operationTables.every((table) =>
      table.cards.every(
        (card) =>
          card.status === CARD_STATUS.NOT_SEEN ||
          card.status === CARD_STATUS.LOCKED
      )
    );
  };

  const showFluencyStatusTooltip = (deck: DeckReport) => {
    return {
      allGroupsLocked: areAllCellsLocked(deck.operationTables),
      noStudentsConnected: hasNoStudentsConnected(deck.operationTables),
    };
  };

  const handleChange = (option: OptionType | null): void => {
    startTableLoading();
    if (option?.value === '') {
      setMode('allClassroomMode');
      setSelectedStudent('');
    } else if (option) {
      setMode('studentMode');
      setSelectedStudent(option.value);
      setEventData('report_fluency_select_student', {
        selected_student_user_uuid: option.value,
      });
    }
  };

  return {
    adaptedStudents,
    allStudents,
    fluencyDashboardReports,
    handleChange,
    mode,
    selectedStudent,
    tableUpdateLoading,
    areAllCellsLocked,
    showFluencyStatusTooltip,
  };
};
